// Gera imagens 16:9 (1920x1080) padrão YouTube dos slides do Palpites Show.
// 100% client-side via Canvas. Zero créditos de IA.

import { PalpitesData } from "./palpitesGenerator";
import { animalMapping } from "@/utils/animalImages";

export const IMG_W = 1920;
export const IMG_H = 1080;

export interface ImagemSlideDef {
  id: string;
  nome: string;
  titulo: string;
  linhas: string[];
  grupos?: string[]; // grupos cujas fotos aparecem
  rodape?: string;
}

export function buildImagemSlides(data: PalpitesData): ImagemSlideDef[] {
  const slides: ImagemSlideDef[] = [];

  slides.push({
    id: "capa",
    nome: "Capa",
    titulo: "PALPITE DO I.A PALPITE GURU",
    linhas: [data.data],
    grupos: data.palpitesOuro.map((p) => p.grupo),
    rodape: "aipalpiteguru.mentepoderosa2030.com",
  });

  slides.push({
    id: "bicho-do-dia",
    nome: "Bicho do Dia",
    titulo: "BICHO DO DIA",
    linhas: [data.bichoDoDia.join("   ")],
  });

  data.palpitesOuro.forEach((p, i) => {
    slides.push({
      id: `ouro-${i + 1}`,
      nome: `Palpite de Ouro ${i + 1}`,
      titulo: `PALPITE DE OURO ${i + 1}`,
      grupos: [p.grupo],
      linhas: [
        `GRUPO ${p.grupo} — ${p.animal.toUpperCase()}`,
        `DEZENA ${p.dezena}`,
        `CENTENAS: ${p.centenas.join("  ")}`,
        `MILHARES: ${p.milhares.join("  ")}`,
      ],
    });
  });

  slides.push({
    id: "passe",
    nome: "Passe Vai e Vem",
    titulo: "PASSE VAI E VEM",
    linhas: data.passeVaiVem.map((p) => `G ${p.g1} - ${p.g2}`),
  });

  slides.push({
    id: "duques",
    nome: "Duques de Dezenas",
    titulo: "DUQUE DE DEZENAS DE OURO",
    linhas: data.duques.map((d) => d.numeros.join(" - ")),
  });

  slides.push({
    id: "ternos",
    nome: "Ternos de Dezenas",
    titulo: "TERNO DE DEZENAS DE OURO",
    linhas: data.ternos.map((t) => t.numeros.join(" - ")),
  });

  slides.push({
    id: "bonus",
    nome: "Bônus + Forte",
    titulo: "BÔNUS + FORTE",
    linhas: [`MILHAR ${data.bonusMilhar}`, `CENTENA ${data.bonusCentena}`],
  });

  slides.push({
    id: "cta",
    nome: "CTA Final",
    titulo: "IA PALPITE GURU",
    linhas: [
      "Palpites com Inteligência Artificial",
      "NOVO: GURU CHAT no ar 24h",
      "Deixe o like e se inscreva no canal!",
    ],
    rodape: "aipalpiteguru.mentepoderosa2030.com",
  });

  return slides;
}

async function carregarImagem(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function desenharCirculo(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cx: number,
  cy: number,
  r: number,
) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.clip();
  const lado = Math.min(img.width, img.height);
  const sx = (img.width - lado) / 2;
  const sy = (img.height - lado) / 2;
  ctx.drawImage(img, sx, sy, lado, lado, cx - r, cy - r, r * 2, r * 2);
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.lineWidth = 8;
  ctx.strokeStyle = "#eab308";
  ctx.stroke();
}

export async function renderImagemSlide(
  slide: ImagemSlideDef,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = IMG_W;
  canvas.height = IMG_H;
  const ctx = canvas.getContext("2d")!;
  const W = IMG_W;
  const H = IMG_H;

  // fundo
  const grad = ctx.createRadialGradient(W / 2, H / 2, 60, W / 2, H / 2, W / 1.1);
  grad.addColorStop(0, "#1a1408");
  grad.addColorStop(1, "#000000");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // banner topo
  const bannerH = 130;
  const bg = ctx.createLinearGradient(0, 0, W, 0);
  bg.addColorStop(0, "#eab308");
  bg.addColorStop(1, "#f59e0b");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, bannerH);
  ctx.fillStyle = "#000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "900 56px system-ui, -apple-system, sans-serif";
  ctx.fillText("IA PALPITE GURU", W / 2, bannerH * 0.4);
  ctx.font = "600 28px system-ui, sans-serif";
  ctx.fillText("Inteligência Artificial do Jogo do Bicho", W / 2, bannerH * 0.78);

  // título
  ctx.fillStyle = "#facc15";
  ctx.font = "900 72px system-ui, sans-serif";
  ctx.shadowColor = "rgba(250,204,21,0.55)";
  ctx.shadowBlur = 24;
  ctx.fillText(slide.titulo, W / 2, bannerH + 90);
  ctx.shadowBlur = 0;

  // fotos dos bichos (mesma fonte de verdade: animalMapping)
  let y = bannerH + 190;
  if (slide.grupos && slide.grupos.length) {
    const grupos = slide.grupos;
    const r = grupos.length > 1 ? 95 : 130;
    const gap = r * 2 + 60;
    const startX = W / 2 - ((grupos.length - 1) * gap) / 2;
    for (let i = 0; i < grupos.length; i++) {
      const info = animalMapping[grupos[i]];
      if (!info) continue;
      const cx = startX + i * gap;
      const cy = y + r;
      const img = await carregarImagem(info.image);
      if (img) desenharCirculo(ctx, img, cx, cy, r);
      ctx.fillStyle = "#fde68a";
      ctx.font = `900 ${grupos.length > 1 ? 30 : 40}px system-ui, sans-serif`;
      ctx.fillText(`${grupos[i]} ${info.name.toUpperCase()}`, cx, cy + r + 40);
    }
    y += r * 2 + 90;
  }

  // linhas
  const disponivel = H - y - 110;
  const linhas = slide.linhas;
  const gapLinha = Math.min(120, disponivel / Math.max(1, linhas.length));
  linhas.forEach((linha, idx) => {
    const isNumeric = /^[\d\s\-·]+$/.test(linha.trim());
    const size = isNumeric ? Math.min(110, gapLinha * 0.85) : Math.min(64, gapLinha * 0.6);
    ctx.font = `900 ${Math.round(size)}px system-ui, sans-serif`;
    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = "rgba(250,204,21,0.6)";
    ctx.shadowBlur = 22;
    ctx.fillText(linha, W / 2, y + gapLinha * idx + gapLinha / 2);
    ctx.shadowBlur = 0;
  });

  if (slide.rodape) {
    ctx.fillStyle = "#facc15";
    ctx.font = "800 38px system-ui, sans-serif";
    ctx.fillText(slide.rodape, W / 2, H - 50);
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Falha ao gerar imagem"))),
      "image/png",
    );
  });
}

export function baixarImagemBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
